import { WorkClassification } from 'src/app/shared/Models/hourentry';
import { WorkClassificationTemplateNode } from './work-classification-template.models';

/** A node of a read-only preview tree. */
export interface PreviewNode {
  id: number;
  name: string;
  description?: string | null;
  children: PreviewNode[];
}

export interface FlatNode {
  node: WorkClassificationTemplateNode;
  depth: number;
}

/** Builds a nested tree from the flat, already naturally ordered node list. */
export function buildPreviewTree(nodes: WorkClassificationTemplateNode[]): PreviewNode[] {
  const byId = new Map<number, PreviewNode>();
  for (const n of nodes) {
    byId.set(n.id, { id: n.id, name: n.name, description: n.description, children: [] });
  }
  const roots: PreviewNode[] = [];
  for (const n of nodes) {
    const preview = byId.get(n.id)!;
    const parent = n.parentId != null ? byId.get(n.parentId) : undefined;
    (parent ? parent.children : roots).push(preview);
  }
  return roots;
}

/** Maps a project classification tree (nested) to preview nodes. */
export function projectTreeToPreview(classifications: WorkClassification[]): PreviewNode[] {
  return classifications.map(c => ({
    id: c.id,
    name: c.name,
    description: c.description,
    children: projectTreeToPreview(c.children ?? []),
  }));
}

/** Depth-first flat list in tree order, used for parent pickers. */
export function flattenNodes(nodes: WorkClassificationTemplateNode[]): FlatNode[] {
  const childrenOf = new Map<number | null, WorkClassificationTemplateNode[]>();
  for (const n of nodes) {
    const key = n.parentId ?? null;
    if (!childrenOf.has(key)) {
      childrenOf.set(key, []);
    }
    childrenOf.get(key)!.push(n);
  }
  const result: FlatNode[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const n of childrenOf.get(parentId) ?? []) {
      result.push({ node: n, depth });
      walk(n.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}

/** Ids of every descendant of a node (not including the node). */
export function descendantIds(nodes: WorkClassificationTemplateNode[], id: number): Set<number> {
  const result = new Set<number>();
  const stack = [id];
  while (stack.length) {
    const current = stack.pop()!;
    for (const n of nodes) {
      if (n.parentId === current && !result.has(n.id)) {
        result.add(n.id);
        stack.push(n.id);
      }
    }
  }
  return result;
}

/** Nodes that may be chosen as a parent for `nodeId`: everything except itself and its subtree. */
export function allowedParents(nodes: WorkClassificationTemplateNode[], nodeId: number | null): FlatNode[] {
  if (nodeId == null) {
    return flattenNodes(nodes);
  }
  const excluded = descendantIds(nodes, nodeId);
  excluded.add(nodeId);
  return flattenNodes(nodes).filter(f => !excluded.has(f.node.id));
}
